// src/components/LearningStreak/LearningStreak.jsx
// MINIMAL CHANGES: Better error handling and token support

import React, { useState, useEffect } from "react";
import axios from "axios";
import { getApiBaseUrl } from "../../services/apiBase.js";
import "./LearningStreak.css";

/**
 * Configuration
 */
const API_BASE_URL = getApiBaseUrl();

/**
 * Get auth token
 */
const getAuthToken = () => {
  return (
    localStorage.getItem("access_token") ||
    localStorage.getItem("token") ||
    sessionStorage.getItem("access_token")
  );
};

/**
 * Default streak state
 */
const DEFAULT_STREAK = {
  current_streak: 0,
  longest_streak: 0,
  last_activity: null,
  streak_status: "inactive",
  total_learning_days: 0,
};

/**
 * API Service
 */
const streakApi = {
  getStreak: async (userId) => {
    if (!userId || userId === "loading") {
      throw new Error("Invalid user ID");
    }

    const token = getAuthToken();

    // CORRECT: Your backend route is /api/streak/{userId} (has /api prefix)
    const response = await axios.get(`${API_BASE_URL}/api/streak/${userId}`, {
      timeout: 10000, // Increased timeout to 10s
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      withCredentials: true, // ADDED: Important for CORS
    });

    return response.data;
  },
};

/**
 * Utility functions
 */
const streakUtils = {
  isStreakActive: (status) => status === "active",
  isStreakAtRisk: (status) => status === "at_risk",
  formatDate: (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString();
  },
  getProgressPercentage: (current, longest) => {
    if (!longest || longest === 0) return 0;
    return Math.min((current / longest) * 100, 100);
  },
};

/**
 * Motivation message component
 */
const MotivationMessage = ({
  currentStreak,
  longestStreak,
  isActive,
  isAtRisk,
}) => {
  if (currentStreak === 0) {
    return (
      <div className="streak-motivation start">
        <p>Start your learning journey today!</p>
        <small>Complete a lesson to begin your streak</small>
      </div>
    );
  }

  if (isAtRisk && currentStreak > 0) {
    return (
      <div className="streak-motivation warning">
        <p>Don't break your streak!</p>
        <small>Learn something today to keep it alive</small>
      </div>
    );
  }

  if (!isActive && !isAtRisk && currentStreak > 0) {
    return (
      <div className="streak-motivation expired">
        <p>Your {currentStreak}-day streak expired</p>
        <small>
          Start fresh - you can beat your record of {longestStreak} {longestStreak === 1 ? "day" : "days"}!
        </small>
      </div>
    );
  }

  if (isActive && currentStreak > 0 && currentStreak < 7) {
    return (
      <div className="streak-motivation active">
        <p>Great start! Keep going!</p>
        <small>Come back tomorrow to grow your streak</small>
      </div>
    );
  }

  if (isActive && currentStreak >= 7 && currentStreak < 30) {
    return (
      <div className="streak-motivation active">
        <p>Amazing! You're on fire!</p>
        <small>Consistency is the key to success</small>
      </div>
    );
  }

  if (isActive && currentStreak >= 30) {
    return (
      <div className="streak-motivation champion">
        <p>Incredible dedication!</p>
        <small>You're a learning champion!</small>
      </div>
    );
  }

  return null;
};

/**
 * Progress bar component
 */
const ProgressBar = ({ currentStreak, longestStreak }) => {
  if (currentStreak === 0 || longestStreak === 0) {
    return null;
  }

  const percentage = streakUtils.getProgressPercentage(
    currentStreak,
    longestStreak
  );
  const isPersonalBest = currentStreak === longestStreak;
  const daysRemaining = longestStreak - currentStreak;

  return (
    <div className="streak-progress">
      <div className="progress-bar">
        <div className="progress-fill" style={{ width: `${percentage}%` }} />
      </div>
      <div className="progress-label">
        {isPersonalBest ? (
          <span>🎉 Personal best!</span>
        ) : (
          <span>{daysRemaining} {daysRemaining === 1 ? "day" : "days"} to beat your record</span>
        )}
      </div>
    </div>
  );
};

/**
 * Details section component
 */
const StreakDetails = ({ longestStreak, totalLearningDays, lastActivity }) => {
  return (
    <div className="streak-details">
      {longestStreak > 0 && (
        <div className="detail-item">
          <span className="detail-label">Best Streak:</span>
          <span className="detail-value">{longestStreak} {longestStreak === 1 ? "day" : "days"}</span>
        </div>
      )}

      <div className="detail-item">
        <span className="detail-label">Learning Days:</span>
        <span className="detail-value">{totalLearningDays || 0}</span>
      </div>

      {lastActivity && (
        <div className="detail-item">
          <span className="detail-label">Last Active:</span>
          <span className="detail-value">
            {streakUtils.formatDate(lastActivity)}
          </span>
        </div>
      )}
    </div>
  );
};

/**
 * Loading state component
 */
const LoadingState = () => (
  <div className="learning-streak-card">
    <div className="streak-loading">
      <div className="loading-spinner"></div>
      <span>Loading streak...</span>
    </div>
  </div>
);

/**
 * Error banner component
 */
const ErrorBanner = ({ error, onRetry }) => (
  <div className="streak-error-banner">
    <span className="error-icon">⚠️</span>
    <span className="error-text">{error}</span>
    {onRetry && (
      <button
        onClick={onRetry}
        style={{
          background: "rgba(255, 255, 255, 0.2)",
          border: "none",
          color: "white",
          padding: "4px 12px",
          borderRadius: "4px",
          cursor: "pointer",
          fontSize: "0.875rem",
          marginLeft: "auto",
        }}
      >
        Retry
      </button>
    )}
  </div>
);

/**
 * Main Learning Streak Component
 */
const LearningStreak = ({ userId }) => {
  const [streakData, setStreakData] = useState(DEFAULT_STREAK);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userId || userId === "loading") {
      setLoading(false);
      return;
    }

    fetchStreakData();

    // Auto-refresh every 5 minutes
    const interval = setInterval(fetchStreakData, 300000);
    return () => clearInterval(interval);
  }, [userId]);

  const fetchStreakData = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log("🔥 Fetching streak for user:", userId);
      const data = await streakApi.getStreak(userId);
      console.log("✅ Streak data received:", data);

      setStreakData(data || DEFAULT_STREAK);
    } catch (err) {
      console.error("❌ Streak fetch error:", err);
      handleApiError(err);
      // Still show default streak instead of breaking
      setStreakData(DEFAULT_STREAK);
    } finally {
      setLoading(false);
    }
  };

  const handleApiError = (err) => {
    let errorMessage = "Could not load streak data";

    if (err.code === "ECONNABORTED") {
      errorMessage = "Request timed out - server may be slow";
    } else if (err.code === "ERR_NETWORK") {
      errorMessage = "Network error - check your connection";
    } else if (err.response?.status === 404) {
      errorMessage = "No streak data yet - start learning to build one!";
      // Don't show error for 404, just use default
      return;
    } else if (err.response?.status === 500 || err.response?.status === 502) {
      errorMessage = "Server temporarily unavailable";
    } else if (err.response?.status === 401) {
      errorMessage = "Please log in to view your streak";
    }

    setError(errorMessage);
  };

  // Extract data with defaults
  const currentStreak = streakData.current_streak || 0;
  const longestStreak = streakData.longest_streak || 0;
  const lastActivity = streakData.last_activity;
  const totalLearningDays = streakData.total_learning_days || 0;
  const status = streakData.streak_status || "inactive";

  const isActive = streakUtils.isStreakActive(status);
  const isAtRisk = streakUtils.isStreakAtRisk(status);

  if (loading) {
    return <LoadingState />;
  }

  return (
    <div
      className={`learning-streak-card ${
        isActive ? "active" : isAtRisk ? "at-risk" : "inactive"
      }`}
    >
      {error && <ErrorBanner error={error} onRetry={fetchStreakData} />}

      <div className="streak-header">
        <h3>🔥 Learning Streak</h3>
        {isActive && <span className="active-badge">Active</span>}
        {isAtRisk && <span className="at-risk-badge">At Risk</span>}
      </div>

      <div className="streak-content">
        <div className="streak-flame">
          <span className={`flame-icon ${currentStreak > 0 ? "burning" : ""}`}>
            🔥
          </span>
        </div>

        <div className="streak-number">
          <span className="number">{currentStreak}</span>
          <span className="label">Day{currentStreak !== 1 ? "s" : ""}</span>
        </div>
      </div>

      <StreakDetails
        longestStreak={longestStreak}
        totalLearningDays={totalLearningDays}
        lastActivity={lastActivity}
      />

      <MotivationMessage
        currentStreak={currentStreak}
        longestStreak={longestStreak}
        isActive={isActive}
        isAtRisk={isAtRisk}
      />

      <ProgressBar
        currentStreak={currentStreak}
        longestStreak={longestStreak}
      />
    </div>
  );
};

export default LearningStreak;
